import React, { ReactNode, Suspense } from "react";
import styled from "styled-components";

import { parseExam } from "@examspark/api";
import { isUserRole } from "@examspark/common";
import {
  ActivityEditorModal,
  PrintExamModal,
  useModal,
  useQueryGetPlayableExam,
  useAppContext,
} from "@examspark/common-app";
import { Button, Loading, Text, Toolbar, VSpace } from "@examspark/common-ui";
import {
  IconPencilLine,
  IconPrintLine,
} from "@examspark/common-ui/dist/components/icons";
import { Exam } from "@examspark/exam-ui";
import { ParsedExam, WorkspaceUserRole } from "@examspark/types";

import { useFlags } from "../../hooks/useFlags";
import { useUsername } from "../../hooks/useUsername";
import PageHead from "../common/PageHead";
import { activityEditorConfig } from "../workspace/WorkspaceAssessments";

const StyledPreviewBox = styled.div`
  border: 1px solid #ccc;
  border-radius: 1rem;
  padding: 1rem;
  font-family:
    proxima-nova,
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    "Roboto",
    "Oxygen",
    "Ubuntu",
    "Cantarell",
    "Fira Sans",
    "Droid Sans",
    "Helvetica Neue",
    sans-serif;

  max-height: 80vh;
  flex-basis: 300px;
  flex-grow: 1;
`;

type Props = {
  examId: string | undefined;
  toolbar?: (props: { exam: ParsedExam; canSeeEdit: boolean }) => ReactNode;
  assessmentCenter?: boolean;
};

const ExamDetail = ({ examId, toolbar, assessmentCenter }: Props) => {
  const [context] = useAppContext();
  const username = useUsername();

  const activityEditorModal = useModal();
  const printExamModal = useModal();

  const flags = useFlags();
  const { data } = useQueryGetPlayableExam({
    id: examId,
    sk: "exam#v0",
  });

  const exam = parseExam(data);

  if (exam == null) {
    return <Loading />;
  }

  const workspaceUser = context.workspaceUser;

  const canSeeEdit =
    ((isUserRole(workspaceUser, WorkspaceUserRole.ContentManager) &&
      exam?.workspaceId === workspaceUser?.id) ||
      exam.creatorId === context.user?.id) &&
    (!!assessmentCenter || !!exam.isUnlisted);

  return (
    <>
      <PrintExamModal
        workspaceId={context.workspace?.id}
        workspaceName={exam?.workspace?.name}
        username={username}
        examId={exam.id}
        requestFeedback={flags.printExamFeedbackEnabled}
        publisherName="TCMPC"
        flags={{ showShuffleItems: flags.showShuffleItems }}
        {...printExamModal.modalProps}
      />
      <ActivityEditorModal
        {...activityEditorModal.modalProps}
        title="Edit Assessment"
        examId={exam.id}
        onSave={() => {
          activityEditorModal.close();
        }}
        editorConfig={activityEditorConfig}
      />
      <VSpace size="3rem">
        <PageHead>
          <>
            <Toolbar>
              <Text.H1>{exam?.name}</Text.H1>
              <Toolbar.Right>
                <Button
                  title="Print"
                  variant="plain"
                  onClick={printExamModal.open}
                >
                  <IconPrintLine width="16" />
                  Print
                </Button>
                {canSeeEdit && (
                  <Button
                    title="Edit Assessment"
                    variant="plain"
                    onClick={activityEditorModal.open}
                  >
                    <IconPencilLine width="16px" />
                  </Button>
                )}
                {toolbar?.({ exam, canSeeEdit })}
              </Toolbar.Right>
            </Toolbar>
          </>
        </PageHead>
        <VSpace>
          <Text.H3>Exam Preview</Text.H3>
          <StyledPreviewBox>
            <Suspense fallback="Loading...">
              {exam && (
                <Exam
                  autoStart
                  examData={exam}
                  flags={flags}
                  disableSubmit
                  disablePause
                  view="instructor"
                />
              )}
            </Suspense>
          </StyledPreviewBox>
        </VSpace>
      </VSpace>
    </>
  );
};

export default ExamDetail;
